import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type ToastType = 'success' | 'info' | 'warning' | 'error';

export interface Toast {
    id: string;
    type: ToastType;
    message: string;
    duration: number;
    paused: boolean;
}

interface TimerData {
    timer: ReturnType<typeof setTimeout> | null;
    startedAt: number;
    remaining: number;
}

@Injectable({ providedIn: 'root' })
export class ToasterService {
    public toasts$: Observable<Toast[]>;
    private toastsSubject = new BehaviorSubject<Toast[]>([]);

    private timerData = new Map<string, TimerData>();

    public constructor() {
        this.toasts$ = this.toastsSubject.asObservable();
    }

    public show(message: string, type: ToastType, duration = 5000): void {
        const id = this.generateId();
        const toast: Toast = {
            id,
            type,
            message,
            duration,
            paused: false,
        };
        this.toastsSubject.next([...this.toastsSubject.value, toast]);
        this.startTimer(id, duration);
    }

    public dismiss(id: string): void {
        this.clearTimer(id);
        this.toastsSubject.next(
            this.toastsSubject.value.filter(t => t.id !== id)
        );
    }

    public pause(id: string): void {
        const data = this.timerData.get(id);
        if (!data || !data.timer) return;

        clearTimeout(data.timer);
        data.timer = null;

        const elapsed = Date.now() - data.startedAt;
        data.remaining = Math.max(0, data.remaining - elapsed);

        this.updateToast(id, { paused: true });
    }

    public resume(id: string): void {
        const data = this.timerData.get(id);
        if (!data || data.timer) return;

        data.startedAt = Date.now();
        data.timer = setTimeout(() => this.dismiss(id), data.remaining);

        this.updateToast(id, { paused: false });
    }

    private updateToast(id: string, changes: Partial<Toast>): void {
        this.toastsSubject.next(
            this.toastsSubject.value.map(t =>
                t.id === id ? { ...t, ...changes } : t
            )
        );
    }

    private startTimer(id: string, duration: number): void {
        const timer = setTimeout(() => this.dismiss(id), duration);
        this.timerData.set(id, {
            timer,
            startedAt: Date.now(),
            remaining: duration,
        });
    }

    private clearTimer(id: string): void {
        const data = this.timerData.get(id);
        if (data?.timer) {
            clearTimeout(data.timer);
        }
        this.timerData.delete(id);
    }

    private generateId(): string {
        return Math.random().toString(36).substring(2, 11);
    }
}
